const STATE_KEY = "hydrotools:point-cloud-workspace:v1";
const ENABLE_ABSOLUTE_Z_CLIP = true;

function esc(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

const ELEVATION_RAMPS = ["VIRIDIS", "INFERNO", "PLASMA", "SPECTRAL", "RAINBOW"];
const INTENSITY_RAMPS = ["GRAYSCALE", "COPPER", "BLUE_ORANGE", "ICE_FIRE"];

function rampsForAttribute(attribute) {
  const a = String(attribute || "").toLowerCase();
  return a === "intensity" ? INTENSITY_RAMPS : ELEVATION_RAMPS;
}

function readState() {
  const fallback = {
    filePath: "",
    maxPreviewPoints: 12000,
    potreeDatasetName: "",
    rasterResolution: 1,
    rasterColorRamp: "terrain",
    rasterStretchMin: 0,
    rasterStretchMax: 100,
    rasterOpacity: 0.92,
    potreePointBudget: 3000000,
    potreePointSize: 0.8,
    potreePointShape: "CIRCLE",
    potreeAxisEnabled: true,
    potreeAxisTicksEnabled: true,
    potreeEdlEnabled: false,
    potreeEdlRadius: 1.0,
    potreeEdlStrength: 0.3,
    potreeEdlOpacity: 1.0,
    potreeAttribute: "classification",
    potreeGradient: "VIRIDIS",
    potreeElevationMin: 0,
    potreeElevationMax: 100,
    potreeZClipEnabled: false,
    potreeZClipMin: 0,
    potreeZClipMax: 100,
    potreeClassVisibility: {
      2: true,
      5: true,
      6: true,
      9: true,
    },
    potreeClassColors: {
      2: "#9ca3af",
      5: "#16a34a",
      6: "#f59e0b",
      9: "#3b82f6",
    },
    potreeLastJobId: "",
    projectFiles: [],
  };
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      ...fallback,
      ...(parsed && typeof parsed === "object" ? parsed : {}),
      maxPreviewPoints: Math.max(1000, Math.min(50000, Number(parsed?.maxPreviewPoints || fallback.maxPreviewPoints) || fallback.maxPreviewPoints)),
      rasterResolution: Math.max(0.1, Math.min(10, Number(parsed?.rasterResolution || fallback.rasterResolution) || fallback.rasterResolution)),
      rasterColorRamp: String(parsed?.rasterColorRamp || fallback.rasterColorRamp || "terrain"),
      rasterStretchMin: Number(parsed?.rasterStretchMin ?? fallback.rasterStretchMin),
      rasterStretchMax: Number(parsed?.rasterStretchMax ?? fallback.rasterStretchMax),
      rasterOpacity: Math.max(0, Math.min(1, Number(parsed?.rasterOpacity ?? fallback.rasterOpacity) || fallback.rasterOpacity)),
      potreePointBudget: Math.max(100000, Math.min(30000000, Number(parsed?.potreePointBudget || fallback.potreePointBudget) || fallback.potreePointBudget)),
      potreePointSize: Math.max(0.1, Math.min(10, Number(parsed?.potreePointSize || fallback.potreePointSize) || fallback.potreePointSize)),
      potreePointShape: String(parsed?.potreePointShape || fallback.potreePointShape || "CIRCLE").toUpperCase(),
      potreeAxisEnabled: parsed?.potreeAxisEnabled == null ? true : !!parsed?.potreeAxisEnabled,
      potreeAxisTicksEnabled: parsed?.potreeAxisTicksEnabled == null ? true : !!parsed?.potreeAxisTicksEnabled,
      potreeEdlEnabled: !!parsed?.potreeEdlEnabled,
      potreeEdlRadius: Math.max(0.1, Math.min(4, Number(parsed?.potreeEdlRadius ?? fallback.potreeEdlRadius) || fallback.potreeEdlRadius)),
      potreeEdlStrength: Math.max(0, Math.min(5, Number(parsed?.potreeEdlStrength ?? fallback.potreeEdlStrength) || fallback.potreeEdlStrength)),
      potreeEdlOpacity: Math.max(0, Math.min(1, Number(parsed?.potreeEdlOpacity ?? fallback.potreeEdlOpacity) || fallback.potreeEdlOpacity)),
      potreeAttribute: String(parsed?.potreeAttribute || fallback.potreeAttribute || "classification"),
      potreeGradient: String(parsed?.potreeGradient || fallback.potreeGradient || "VIRIDIS").toUpperCase(),
      potreeElevationMin: Number(parsed?.potreeElevationMin ?? fallback.potreeElevationMin),
      potreeElevationMax: Number(parsed?.potreeElevationMax ?? fallback.potreeElevationMax),
      potreeZClipEnabled: !!parsed?.potreeZClipEnabled,
      potreeZClipMin: Number(parsed?.potreeZClipMin ?? fallback.potreeZClipMin),
      potreeZClipMax: Number(parsed?.potreeZClipMax ?? fallback.potreeZClipMax),
      potreeClassVisibility: {
        ...fallback.potreeClassVisibility,
        ...(parsed?.potreeClassVisibility && typeof parsed.potreeClassVisibility === "object" ? parsed.potreeClassVisibility : {}),
      },
      potreeClassColors: {
        ...fallback.potreeClassColors,
        ...(parsed?.potreeClassColors && typeof parsed.potreeClassColors === "object" ? parsed.potreeClassColors : {}),
      },
      potreeLastJobId: String(parsed?.potreeLastJobId || fallback.potreeLastJobId || ""),
      projectFiles: Array.isArray(parsed?.projectFiles)
        ? parsed.projectFiles
          .map((item) => ({
            id: String(item?.id || `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`),
            name: String(item?.name || item?.sourcePath || "Point Cloud"),
            sourcePath: String(item?.sourcePath || ""),
            potreeJobId: String(item?.potreeJobId || ""),
            status: String(item?.status || "ready"),
          }))
          .filter((item) => item.sourcePath)
        : [],
    };
  } catch {
    return fallback;
  }
}

function writeState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    body: options.body == null ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(payload?.error || `Request failed (${response.status})`));
  return payload;
}

const THREE_LIBS = {
  cache: null,
  async load() {
    if (!this.cache) {
      this.cache = (async () => {
        const THREE = await import("three");
        let controls = null;
        try {
          controls = await import("three/examples/jsm/controls/OrbitControls.js");
        } catch {
          controls = await import("three/addons/controls/OrbitControls.js");
        }
        return {
          THREE,
          OrbitControls: controls.OrbitControls,
        };
      })();
    }
    return this.cache;
  },
};

export const pointCloudWorkspaceTool = {
  id: "point-cloud-workspace",
  name: "Point Cloud Workspace",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  const Z_NUDGE_STEP = 0.1;
  let containerEl = null;
  let busy = false;
  let state = readState();
  let status = "";
  let dataset = null;
  let potreeBusy = false;
  let potreeJob = null;
  let potreePollTimer = null;
  let projectJobsPollTimer = null;
  let potreeViewerWindow = null;
  let projectAutoOpenFirstCompleted = false;
  let lastRangeBoundsKey = "";
  let preview = {
    destroy: null,
  };
  const makeProjectItemId = () => `pcf-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

  function persist() {
    state.potreeLastJobId = String(potreeJob?.id || state.potreeLastJobId || "");
    writeState(state);
  }

  function pullStateFromForm() {
    if (!containerEl) return;
    state.filePath = String(containerEl.querySelector("#pcw-file-path")?.value || "").trim();
    state.maxPreviewPoints = Math.max(1000, Math.min(50000, Number(containerEl.querySelector("#pcw-max-points")?.value || 12000) || 12000));
    state.potreeDatasetName = String(containerEl.querySelector("#pcw-potree-name")?.value || "").trim();
    state.rasterResolution = Math.max(0.1, Math.min(10, Number(containerEl.querySelector("#pcw-raster-resolution")?.value || state.rasterResolution || 1) || 1));
    state.rasterColorRamp = String(containerEl.querySelector("#pcw-raster-ramp")?.value || state.rasterColorRamp || "terrain");
    state.rasterStretchMin = Number(containerEl.querySelector("#pcw-raster-min")?.value ?? state.rasterStretchMin ?? 0);
    state.rasterStretchMax = Number(containerEl.querySelector("#pcw-raster-max")?.value ?? state.rasterStretchMax ?? 100);
    state.rasterOpacity = Math.max(0, Math.min(1, Number(containerEl.querySelector("#pcw-raster-opacity")?.value ?? state.rasterOpacity ?? 0.92) || 0.92));
    state.potreePointBudget = Math.max(100000, Math.min(30000000, Number(containerEl.querySelector("#pcw-potree-budget")?.value || 3000000) || 3000000));
    state.potreePointSize = Math.max(0.1, Math.min(10, Number(containerEl.querySelector("#pcw-potree-size")?.value || 0.8) || 0.8));
    state.potreePointShape = String(containerEl.querySelector("#pcw-potree-shape")?.value || "CIRCLE").toUpperCase();
    state.potreeAxisEnabled = !!containerEl.querySelector("#pcw-potree-axis-enabled")?.checked;
    state.potreeAxisTicksEnabled = !!containerEl.querySelector("#pcw-potree-axis-ticks-enabled")?.checked;
    state.potreeEdlEnabled = !!containerEl.querySelector("#pcw-potree-edl-enabled")?.checked;
    state.potreeEdlRadius = Math.max(0.1, Math.min(4, Number(containerEl.querySelector("#pcw-potree-edl-radius")?.value ?? state.potreeEdlRadius ?? 1) || 1));
    state.potreeEdlStrength = Math.max(0, Math.min(5, Number(containerEl.querySelector("#pcw-potree-edl-strength")?.value ?? state.potreeEdlStrength ?? 0.3) || 0.3));
    state.potreeEdlOpacity = Math.max(0, Math.min(1, Number(containerEl.querySelector("#pcw-potree-edl-opacity")?.value ?? state.potreeEdlOpacity ?? 1) || 1));
    state.potreeAttribute = String(containerEl.querySelector("#pcw-potree-attribute")?.value || "classification");
    state.potreeGradient = String(containerEl.querySelector("#pcw-potree-gradient")?.value || "VIRIDIS").toUpperCase();
    state.potreeElevationMin = Number(state.potreeElevationMin ?? 0);
    state.potreeElevationMax = Number(state.potreeElevationMax ?? 100);
    state.potreeZClipEnabled = !!containerEl.querySelector("#pcw-potree-zclip-enabled")?.checked;
    state.potreeZClipMin = Number(
      containerEl.querySelector("#pcw-potree-zclip-min-slider")?.value ??
      state.potreeZClipMin ??
      0
    );
    state.potreeZClipMax = Number(
      containerEl.querySelector("#pcw-potree-zclip-max-slider")?.value ??
      containerEl.querySelector("#pcw-potree-zclip-max")?.value ??
      state.potreeZClipMax ??
      100
    );
    const classIds = discoveredClassIds();
    for (const id of classIds) {
      const vis = containerEl.querySelector(`#pcw-class-vis-${id}`);
      const col = containerEl.querySelector(`#pcw-class-color-${id}`);
      if (vis) state.potreeClassVisibility[id] = !!vis.checked;
      if (col && col.value) state.potreeClassColors[id] = String(col.value);
    }
    persist();
  }

  function currentPotreeStyle() {
    const zBounds = potreeJob?.bounds || dataset?.fullBounds || dataset?.bounds || {};
    const zMin = Number.isFinite(Number(zBounds?.minZ)) ? Number(zBounds.minZ) : 0;
    const zMax = Number.isFinite(Number(zBounds?.maxZ)) ? Number(zBounds.maxZ) : 100;
    const minIn = Number(state.potreeElevationMin);
    const maxIn = Number(state.potreeElevationMax);
    const eMin = Number.isFinite(minIn) ? minIn : zMin;
    const eMax = Number.isFinite(maxIn) ? Math.max(eMin, maxIn) : zMax;
    return {
      pointBudget: state.potreePointBudget,
      pointSize: state.potreePointSize,
      pointShape: state.potreePointShape,
      axisEnabled: state.potreeAxisEnabled !== false,
      axisTicksEnabled: state.potreeAxisTicksEnabled !== false,
      edlEnabled: !!state.potreeEdlEnabled,
      edlRadius: Number(state.potreeEdlRadius),
      edlStrength: Number(state.potreeEdlStrength),
      edlOpacity: Number(state.potreeEdlOpacity),
      attribute: state.potreeAttribute,
      gradient: state.potreeGradient,
      elevationMin: eMin,
      elevationMax: eMax,
      datasetZMin: zMin,
      datasetZMax: zMax,
      zClipEnabled: !!state.potreeZClipEnabled,
      zClipMin: Number(state.potreeZClipMin),
      zClipMax: Number(state.potreeZClipMax),
      classVisibility: { ...(state.potreeClassVisibility || {}) },
      classColors: { ...(state.potreeClassColors || {}) },
    };
  }

  function reconcileRangesToCurrentBounds({ resetToFull = false } = {}) {
    const zBounds = potreeJob?.bounds || dataset?.fullBounds || dataset?.bounds || {};
    const zMin = Number.isFinite(Number(zBounds?.minZ)) ? Number(zBounds.minZ) : 0;
    const zMax = Number.isFinite(Number(zBounds?.maxZ)) ? Number(zBounds.maxZ) : 100;
    const lo = Math.min(zMin, zMax);
    const hi = Math.max(zMin, zMax);
    const boundsKey = `${lo}|${hi}`;
    const boundsChanged = boundsKey !== lastRangeBoundsKey;
    if (boundsChanged) lastRangeBoundsKey = boundsKey;

    const clamp = (v, fallback) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return fallback;
      return Math.max(lo, Math.min(hi, n));
    };

    if (resetToFull || boundsChanged) {
      state.potreeElevationMin = Number(oneDecimal(lo, lo));
      state.potreeElevationMax = Number(oneDecimal(hi, hi));
      state.potreeZClipMin = Number(oneDecimal(lo, lo));
      state.potreeZClipMax = Number(oneDecimal(hi, hi));
      persist();
      return;
    }

    let elevMin = clamp(state.potreeElevationMin, lo);
    let elevMax = clamp(state.potreeElevationMax, hi);
    if (elevMax < elevMin) [elevMin, elevMax] = [elevMax, elevMin];

    let clipMin = clamp(state.potreeZClipMin, lo);
    let clipMax = clamp(state.potreeZClipMax, hi);
    if (clipMax < clipMin) [clipMin, clipMax] = [clipMax, clipMin];

    state.potreeElevationMin = Number(oneDecimal(elevMin, lo));
    state.potreeElevationMax = Number(oneDecimal(elevMax, hi));
    state.potreeZClipMin = Number(oneDecimal(clipMin, lo));
    state.potreeZClipMax = Number(oneDecimal(clipMax, hi));
    persist();
  }

  function pushPotreeStyleToViewer() {
    if (!potreeJob?.id && !app?.view?.getPotreeWindow?.()) return;
    const style = currentPotreeStyle();
    const payload = {
      type: "hydrotools-potree-style",
      jobId: String(potreeJob?.id || ""),
      style,
    };
    try {
      localStorage.setItem("hydrotools:potree-style:last", JSON.stringify({ ...payload, ts: Date.now() }));
    } catch {}
    try {
      const channel = new BroadcastChannel("hydrotools-potree-controls");
      channel.postMessage(payload);
      channel.close();
    } catch {}
    try {
      if (potreeViewerWindow && !potreeViewerWindow.closed) {
        if (typeof potreeViewerWindow.__hydrotoolsApplyStyle === "function") {
          potreeViewerWindow.__hydrotoolsApplyStyle(style);
        }
        potreeViewerWindow.postMessage(payload, window.location.origin);
      }
    } catch {}
    try {
      const iframeWin = app?.view?.getPotreeWindow?.();
      if (iframeWin) {
        if (typeof iframeWin.__hydrotoolsApplyStyle === "function") {
          iframeWin.__hydrotoolsApplyStyle(style);
        }
        iframeWin.postMessage(payload, window.location.origin);
      }
    } catch {}
  }

  function pushPotreeCommandToViewer(command, args = {}) {
    if (!potreeJob?.id) return;
    const payload = {
      type: "hydrotools-potree-command",
      jobId: String(potreeJob.id),
      command: String(command || ""),
      args: args && typeof args === "object" ? args : {},
    };
    try {
      localStorage.setItem("hydrotools:potree-command:last", JSON.stringify({ ...payload, ts: Date.now() }));
    } catch {}
    try {
      const channel = new BroadcastChannel("hydrotools-potree-controls");
      channel.postMessage(payload);
      channel.close();
    } catch {}
    try {
      if (potreeViewerWindow && !potreeViewerWindow.closed) {
        if (typeof potreeViewerWindow.__hydrotoolsApplyCommand === "function") {
          potreeViewerWindow.__hydrotoolsApplyCommand(String(command || ""));
        }
        potreeViewerWindow.postMessage(payload, window.location.origin);
      }
    } catch {}
    try {
      const iframeWin = app?.view?.getPotreeWindow?.();
      if (iframeWin) {
        if (typeof iframeWin.__hydrotoolsApplyCommand === "function") {
          iframeWin.__hydrotoolsApplyCommand(String(command || ""));
        }
        iframeWin.postMessage(payload, window.location.origin);
      }
    } catch {}
  }

  function scorePotreeJob(job) {
    const status = String(job?.status || "").toLowerCase();
    if (status === "running" || status === "queued" || status === "prepare" || status === "convert" || status === "postprocess") return 4;
    if (status === "completed") return 3;
    if (status === "failed" || status === "canceled") return 2;
    return 1;
  }

  function buildHostedTiffUrl(filePath) {
    return `/api/qimera/local-file?path=${encodeURIComponent(String(filePath || ""))}&v=${Date.now()}`;
  }

  function addRasterLayer(kind, filePath, layerName) {
    if (!filePath) return;
    const layersService = app?.services?.layers;
    if (!layersService) return;
    const existing = (layersService.listLayers?.() || []).find((layer) =>
      String(layer?.meta?.pointCloudRasterPath || "").trim().toLowerCase() === String(filePath).toLowerCase()
    );
    const isShade = String(kind) === "hillshade";
    const stretchMin = Number(state.rasterStretchMin);
    const stretchMax = Number(state.rasterStretchMax);
    const def = {
      id: existing?.id || undefined,
      type: "geotiff-url",
      kind: "overlay",
      name: layerName,
      url: buildHostedTiffUrl(filePath),
      opacity: existing?.opacity ?? state.rasterOpacity ?? 0.92,
      visible: true,
      meta: {
        sourceType: "point-cloud-workspace",
        rasterKind: kind,
        pointCloudRasterPath: filePath,
      },
      options: {
        autoZoom: kind === "hillshade",
        colorRamp: isShade ? "grayscale" : String(state.rasterColorRamp || "terrain").toLowerCase(),
        stretchMin: Number.isFinite(stretchMin) ? stretchMin : undefined,
        stretchMax: Number.isFinite(stretchMax) ? stretchMax : undefined,
      },
    };
    if (existing?.id) layersService.removeLayer(existing.id);
    layersService.addLayer("point-cloud-workspace", def);
  }

  function applyRasterStyleToGeneratedLayers() {
    const layersService = app?.services?.layers;
    if (!layersService) return;
    pullStateFromForm();
    const layers = layersService.listLayers?.() || [];
    const targets = layers.filter((layer) => String(layer?.meta?.sourceType || "") === "point-cloud-workspace");
    for (const layer of targets) {
      const kind = String(layer?.meta?.rasterKind || "");
      const filePath = String(layer?.meta?.pointCloudRasterPath || "");
      if (!filePath) continue;
      const name = String(layer?.name || kind || "Raster");
      if (layer?.id) layersService.removeLayer(layer.id);
      addRasterLayer(kind, filePath, name);
    }
    status = `Applied raster style to ${targets.length} layer(s).`;
    render();
  }

  async function generateAndLoad2DLayers() {
    pullStateFromForm();
    const filePath = String(state.filePath || "").trim();
    if (!filePath) {
      status = "Open a LAS/LAZ dataset first.";
      render();
      return;
    }
    potreeBusy = true;
    status = "Generating DSM/DTM/Hillshade rasters...";
    render();
    try {
      const payload = await requestJson("/api/image-generator/las-products", {
        method: "POST",
        body: {
          filePath,
          resolution: Number(state.rasterResolution || 1),
          minAboveWater: 0.5,
          maxAboveWater: 6,
        },
      });
      const products = payload?.products || {};
      const stem = String(state.potreeDatasetName || dataset?.format || "Point Cloud");
      addRasterLayer("hillshade", products.hillshade, `${stem} Hillshade`);
      addRasterLayer("dsm", products.dsm || products.elevation, `${stem} DSM`);
      addRasterLayer("dtm", products.dtm, `${stem} DTM`);
      status = "Loaded DSM/DTM/Hillshade as map layers.";
    } catch (error) {
      status = `Could not generate/load 2D layers: ${String(error?.message || error)}`;
    } finally {
      potreeBusy = false;
      render();
    }
  }

  async function findMatchingPotreeJob() {
    const payload = await requestJson("/api/point-cloud/potree/jobs");
    const jobs = Array.isArray(payload?.jobs) ? payload.jobs : [];
    if (!jobs.length) return null;
    const sourcePath = String(state.filePath || "").trim().toLowerCase();
    const datasetName = String(state.potreeDatasetName || "").trim().toLowerCase();
    const lastId = String(state.potreeLastJobId || "").trim();
    let candidates = jobs;
    if (lastId) {
      const byId = jobs.find((j) => String(j?.id || "") === lastId);
      if (byId) {
        const byIdSource = String(byId?.sourcePath || "").trim().toLowerCase();
        // Only trust remembered job id when it matches the currently selected source.
        if (!sourcePath || (byIdSource && byIdSource === sourcePath)) return byId;
      }
    }
    if (sourcePath) {
      const byPath = jobs.filter((j) => String(j?.sourcePath || "").trim().toLowerCase() === sourcePath);
      if (byPath.length) candidates = byPath;
    } else if (datasetName) {
      const byName = jobs.filter((j) => String(j?.datasetName || "").trim().toLowerCase() === datasetName);
      if (byName.length) candidates = byName;
    }
    candidates.sort((a, b) => {
      const ds = scorePotreeJob(b) - scorePotreeJob(a);
      if (ds) return ds;
      const ta = Date.parse(String(a?.updatedAt || a?.createdAt || 0)) || 0;
      const tb = Date.parse(String(b?.updatedAt || b?.createdAt || 0)) || 0;
      return tb - ta;
    });
    return candidates[0] || null;
  }

  async function refreshOrAttachPotreeJob() {
    if (potreeJob?.id) {
      await refreshPotreeJob();
      return;
    }
    try {
      const matched = await findMatchingPotreeJob();
      if (!matched) {
        status = "No matching Potree job found for current dataset.";
        render();
        return;
      }
      potreeJob = matched;
      state.potreeLastJobId = String(matched.id || "");
      persist();
      const jobStatus = String(matched?.status || "").toLowerCase();
      if (!["completed", "failed", "canceled"].includes(jobStatus)) {
        stopPotreePoll();
        potreePollTimer = setInterval(refreshPotreeJob, 1500);
      }
      await refreshPotreeJob();
    } catch (error) {
      status = String(error?.message || error || "Could not refresh Potree job.");
      render();
    }
  }

  function upsertProjectFile(entry) {
    const src = String(entry?.sourcePath || "").trim();
    if (!src) return;
    const existingIdx = (state.projectFiles || []).findIndex((item) =>
      String(item?.sourcePath || "").trim().toLowerCase() === src.toLowerCase()
    );
    const nextItem = {
      id: String(entry?.id || makeProjectItemId()),
      name: String(entry?.name || src.split(/[\\/]/).pop() || "Point Cloud"),
      sourcePath: src,
      potreeJobId: String(entry?.potreeJobId || ""),
      status: String(entry?.status || "ready"),
      progressPct: Number.isFinite(Number(entry?.progressPct)) ? Number(entry.progressPct) : 0,
      viewerUrl: String(entry?.viewerUrl || ""),
    };
    if (existingIdx >= 0) {
      state.projectFiles[existingIdx] = { ...state.projectFiles[existingIdx], ...nextItem };
    } else {
      state.projectFiles = [...(state.projectFiles || []), nextItem];
    }
    persist();
  }

  function removeProjectFile(itemId) {
    const id = String(itemId || "");
    state.projectFiles = (state.projectFiles || []).filter((item) => String(item?.id || "") !== id);
    persist();
  }

  async function addProjectFilesFromPicker() {
    const input = containerEl?.querySelector("#pcw-project-files-input");
    const files = [...(input?.files || [])];
    if (!files.length) return;
    potreeBusy = true;
    status = `Uploading ${files.length} file(s) to project...`;
    render();
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        status = `Uploading ${i + 1}/${files.length}: ${String(file?.name || "point-cloud.las")}`;
        render();
        const uploadRes = await fetch("/api/point-cloud/potree/upload", {
          method: "POST",
          headers: {
            "Content-Type": "application/octet-stream",
            "x-file-name": String(file?.name || "point-cloud.las"),
          },
          body: file,
        });
        const uploadBody = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok || !uploadBody?.sourcePath) {
          throw new Error(String(uploadBody?.error || "potree_upload_failed"));
        }
        upsertProjectFile({
          name: String(file?.name || ""),
          sourcePath: String(uploadBody.sourcePath || ""),
          status: "ready",
        });
      }
      status = `Added ${files.length} file(s) to project.`;
    } catch (error) {
      status = `Could not add project files: ${String(error?.message || error)}`;
    } finally {
      potreeBusy = false;
      if (input) input.value = "";
      render();
    }
  }

  async function generatePotreeForProjectFiles() {
    const items = (state.projectFiles || []).filter((item) => String(item?.sourcePath || "").trim());
    if (!items.length) {
      status = "Add LAS/LAZ files to the project first.";
      render();
      return;
    }
    potreeBusy = true;
    projectAutoOpenFirstCompleted = true;
    startProjectJobsPoll();
    status = `Queueing Potree jobs for ${items.length} file(s)...`;
    render();
    try {
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        status = `Queueing ${i + 1}/${items.length}: ${item.name}`;
        render();
        const startRes = await requestJson("/api/point-cloud/potree/jobs", {
          method: "POST",
          body: {
            datasetName: String(item.name || `Point Cloud ${i + 1}`),
            sourcePath: String(item.sourcePath || ""),
          },
        });
        const jobId = String(startRes?.job?.id || "");
        upsertProjectFile({
          ...item,
          potreeJobId: jobId,
          status: jobId ? "queued" : "failed",
        });
      }
      status = "Potree jobs queued for project files.";
      await refreshProjectFileJobStatuses();
    } catch (error) {
      status = `Could not queue Potree jobs: ${String(error?.message || error)}`;
    } finally {
      potreeBusy = false;
      render();
    }
  }

  async function refreshProjectFileJobStatuses() {
    try {
      const payload = await requestJson("/api/point-cloud/potree/jobs");
      const jobs = Array.isArray(payload?.jobs) ? payload.jobs : [];
      const byId = new Map(jobs.map((j) => [String(j?.id || ""), j]));
      let firstCompletedViewerUrl = "";
      let hasActive = false;
      state.projectFiles = (state.projectFiles || []).map((item) => {
        const jobId = String(item?.potreeJobId || "");
        if (!jobId) return item;
        const job = byId.get(jobId);
        const nextStatus = String(job?.status || item?.status || "queued");
        if (["running", "queued", "prepare", "convert", "postprocess"].includes(nextStatus)) hasActive = true;
        const progressPct = Number(job?.progressPct);
        const viewerUrl = String(job?.viewerUrl || item?.viewerUrl || "");
        if (!firstCompletedViewerUrl && nextStatus === "completed" && viewerUrl) {
          firstCompletedViewerUrl = viewerUrl;
        }
        return {
          ...item,
          status: nextStatus,
          progressPct: Number.isFinite(progressPct) ? progressPct : (Number(item?.progressPct) || 0),
          viewerUrl,
        };
      });
      persist();
      render();
      if (!hasActive) stopProjectJobsPoll();
      if (projectAutoOpenFirstCompleted && firstCompletedViewerUrl) {
        projectAutoOpenFirstCompleted = false;
        openPotreeViewer(firstCompletedViewerUrl);
      }
    } catch {}
  }

  function openPotreeViewer(url) {
    const viewerUrl = String(url || "").trim();
    if (!viewerUrl) return;
    if (app?.view?.showPotree) {
      app.view.showPotree(viewerUrl);
      potreeViewerWindow = app?.view?.getPotreeWindow?.() || null;
    } else {
      potreeViewerWindow = window.open(viewerUrl, "_blank");
    }
    setTimeout(() => {
      potreeViewerWindow = app?.view?.getPotreeWindow?.() || potreeViewerWindow;
      pushPotreeStyleToViewer();
    }, 700);
  }

  function formatNumber(value, digits = 2) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString(undefined, { maximumFractionDigits: digits });
  }

  function projectJobIcon(item) {
    const status = String(item?.status || "").toLowerCase();
    const pct = Number(item?.progressPct);
    if (status === "completed" || (Number.isFinite(pct) && pct >= 100)) {
      return `<span style="color:#22c55e; font-weight:700;" title="Completed">✓</span>`;
    }
    if (status === "failed" || status === "canceled") {
      return `<span style="color:#ef4444; font-weight:700;" title="${esc(status)}">✕</span>`;
    }
    if (status === "uploading" || (Number.isFinite(pct) && pct <= 12)) {
      return `<span style="color:#38bdf8; font-weight:700;" title="Uploading">↑</span>`;
    }
    if (status === "convert" || (Number.isFinite(pct) && pct >= 30 && pct < 80)) {
      return `<span style="color:#f59e0b; font-weight:700;" title="Converting">◔</span>`;
    }
    if (status === "postprocess" || (Number.isFinite(pct) && pct >= 80 && pct < 100)) {
      return `<span style="color:#a78bfa; font-weight:700;" title="Finalizing">◕</span>`;
    }
    if (status === "queued" || status === "prepare" || status === "running") {
      return `<span style="color:#94a3b8; font-weight:700;" title="${esc(status || "Queued")}">…</span>`;
    }
    return `<span style="color:#94a3b8; font-weight:700;" title="Ready">•</span>`;
  }

  function gradientStops(name) {
    const key = String(name || "VIRIDIS").toUpperCase();
    if (key === "COPPER") return ["#1f1300", "#ffb347"];
    if (key === "BLUE_ORANGE") return ["#0b3b8c", "#f59e0b"];
    if (key === "ICE_FIRE") return ["#dbeafe", "#dc2626"];
    if (key === "INFERNO") return ["#000004", "#420a68", "#932567", "#dd513a", "#fca50a", "#fcffa4"];
    if (key === "PLASMA") return ["#0d0887", "#6a00a8", "#b12a90", "#e16462", "#fca636", "#f0f921"];
    if (key === "SPECTRAL") return ["#5e4fa2", "#3288bd", "#66c2a5", "#abdda4", "#e6f598", "#fdae61", "#f46d43", "#9e0142"];
    if (key === "RAINBOW") return ["#6e40aa", "#3b8eea", "#29c7ac", "#8bd646", "#f9cf3a", "#f08228", "#da2f47"];
    if (key === "GRAYSCALE") return ["#111827", "#f8fafc"];
    return ["#440154", "#3b528b", "#21918c", "#5ec962", "#fde725"];
  }

  function gradientCss(name) {
    const stops = gradientStops(name);
    return `linear-gradient(90deg, ${stops.join(", ")})`;
  }

  function oneDecimal(value, fallback = 0) {
    const n = Number(value);
    const v = Number.isFinite(n) ? n : Number(fallback);
    return (Math.round(v * 10) / 10).toFixed(1);
  }

  function buildElevationHistogramSvg(points, minZ, maxZ, bins = 48, selMin = null, selMax = null) {
    const zs = [];
    for (const p of (points || [])) {
      const z = Number(p?.z);
      if (Number.isFinite(z)) zs.push(z);
    }
    if (!zs.length) return `<div class="small" style="opacity:.75;">No preview elevations for histogram.</div>`;
    const lo = Number.isFinite(minZ) ? minZ : Math.min(...zs);
    const hi = Number.isFinite(maxZ) ? maxZ : Math.max(...zs);
    const span = Math.max(1e-9, hi - lo);
    const counts = Array.from({ length: bins }, () => 0);
    for (const z of zs) {
      if (z < lo || z > hi) continue;
      let i = Math.floor(((z - lo) / span) * bins);
      i = Math.max(0, Math.min(bins - 1, i));
      counts[i] += 1;
    }
    const maxCount = Math.max(...counts, 1);
    const width = 540;
    const height = 120;
    const pad = 10;
    const innerW = width - pad * 2;
    const innerH = height - pad * 2;
    const barW = innerW / bins;
    const bars = counts.map((c, i) => {
      const h = Math.max(1, (c / maxCount) * innerH);
      const x = pad + (i * barW);
      const y = pad + (innerH - h);
      return `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${Math.max(1, barW - 0.6).toFixed(2)}" height="${h.toFixed(2)}" rx="1.5" fill="url(#pcwHistGrad)" />`;
    }).join("");
    const sMin = Number.isFinite(Number(selMin)) ? Number(selMin) : lo;
    const sMax = Number.isFinite(Number(selMax)) ? Number(selMax) : hi;
    const clampedMin = Math.max(lo, Math.min(hi, Math.min(sMin, sMax)));
    const clampedMax = Math.max(lo, Math.min(hi, Math.max(sMin, sMax)));
    const minX = pad + (((clampedMin - lo) / span) * innerW);
    const maxX = pad + (((clampedMax - lo) / span) * innerW);
    return `
      <svg viewBox="0 0 ${width} ${height}" width="100%" height="${height}" preserveAspectRatio="none" style="display:block; border:1px solid rgba(148,163,184,.22); border-radius:8px; background:linear-gradient(180deg, rgba(2,6,23,.55), rgba(2,6,23,.75));">
        <defs>
          <linearGradient id="pcwHistGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#22d3ee"/>
            <stop offset="100%" stop-color="#0ea5e9"/>
          </linearGradient>
        </defs>
        <rect x="${minX.toFixed(2)}" y="${pad}" width="${Math.max(1, maxX - minX).toFixed(2)}" height="${innerH}" fill="rgba(245,158,11,.16)" />
        ${bars}
        <line x1="${minX.toFixed(2)}" y1="${pad}" x2="${minX.toFixed(2)}" y2="${(pad + innerH).toFixed(2)}" stroke="#f59e0b" stroke-width="2" />
        <line x1="${maxX.toFixed(2)}" y1="${pad}" x2="${maxX.toFixed(2)}" y2="${(pad + innerH).toFixed(2)}" stroke="#f97316" stroke-width="2" />
      </svg>
    `;
  }

  function setElevationRangeInUI(nextMin, nextMax) {
    const lo = Math.min(Number(nextMin), Number(nextMax));
    const hi = Math.max(Number(nextMin), Number(nextMax));
    const loTxt = oneDecimal(lo, lo);
    const hiTxt = oneDecimal(hi, hi);
    state.potreeElevationMin = Number(loTxt);
    state.potreeElevationMax = Number(hiTxt);
    persist();
  }

  function nudgeClipValue(kind, delta) {
    const zBounds = potreeJob?.bounds || dataset?.fullBounds || dataset?.bounds || {};
    const zMin = Number.isFinite(Number(zBounds?.minZ)) ? Number(zBounds.minZ) : 0;
    const zMax = Number.isFinite(Number(zBounds?.maxZ)) ? Number(zBounds.maxZ) : 100;
    const curMin = Number(state.potreeZClipMin ?? zMin);
    const curMax = Number(state.potreeZClipMax ?? zMax);
    if (kind === "max") {
      const next = Math.max(curMin, Math.min(zMax, curMax + delta));
      state.potreeZClipMax = Math.round(next * 10) / 10;
    } else {
      const next = Math.min(curMax, Math.max(zMin, curMin + delta));
      state.potreeZClipMin = Math.round(next * 10) / 10;
    }
  }

  function asprsClassName(code) {
    const n = Number(code);
    if (n === 0) return "Created, Never Classified";
    if (n === 1) return "Unclassified";
    if (n === 2) return "Ground";
    if (n === 3) return "Low Vegetation";
    if (n === 4) return "Medium Vegetation";
    if (n === 5) return "High Vegetation";
    if (n === 6) return "Building";
    if (n === 7) return "Low Point (Noise)";
    if (n === 8) return "Model Key-Point";
    if (n === 9) return "Water";
    if (n === 10) return "Rail";
    if (n === 11) return "Road Surface";
    if (n === 12) return "Overlap";
    if (n === 13) return "Wire Guard";
    if (n === 14) return "Wire Conductor";
    if (n === 15) return "Transmission Tower";
    if (n === 16) return "Wire-Structure Connector";
    if (n === 17) return "Bridge Deck";
    if (n === 18) return "High Noise";
    return "Unknown";
  }

  function discoveredClassIds() {
    const keys = Object.keys(dataset?.classHistogram || {});
    const ids = keys
      .map((k) => Number(k))
      .filter((v) => Number.isInteger(v) && v >= 0)
      .sort((a, b) => a - b);
    return ids.length ? ids : [2, 5, 6, 9];
  }

  function render() {
    if (!containerEl) return;
    const fullBounds = potreeJob?.bounds || dataset?.fullBounds || dataset?.bounds || null;
    const previewBounds = dataset?.preview?.bounds || dataset?.bounds || null;
    const points = Array.isArray(dataset?.preview?.points) ? dataset.preview.points : [];
    const zMin = Number.isFinite(Number(fullBounds?.minZ)) ? Number(fullBounds.minZ) : 0;
    const zMax = Number.isFinite(Number(fullBounds?.maxZ)) ? Number(fullBounds.maxZ) : 100;
    const elevMin = Number.isFinite(Number(state.potreeElevationMin)) ? Number(state.potreeElevationMin) : zMin;
    const elevMax = Number.isFinite(Number(state.potreeElevationMax)) ? Number(state.potreeElevationMax) : zMax;
    const zClipMin = Number.isFinite(Number(state.potreeZClipMin)) ? Number(state.potreeZClipMin) : zMin;
    const zClipMax = Number.isFinite(Number(state.potreeZClipMax)) ? Number(state.potreeZClipMax) : zMax;
    const histDomainMin = state.potreeZClipEnabled ? Math.min(zClipMin, zClipMax) : zMin;
    const histDomainMax = state.potreeZClipEnabled ? Math.max(zClipMin, zClipMax) : zMax;
    const histSvg = buildElevationHistogramSvg(points, histDomainMin, histDomainMax, 104, elevMin, elevMax);
    const classIds = discoveredClassIds();
    const isElevation = String(state.potreeAttribute) === "elevation";
    const isIntensity = String(state.potreeAttribute) === "intensity";
    const showRampStyling = isElevation || isIntensity;
    const rampOptions = rampsForAttribute(state.potreeAttribute);
    const selectedRamp = rampOptions.includes(String(state.potreeGradient || "").toUpperCase())
      ? String(state.potreeGradient || "").toUpperCase()
      : rampOptions[0];
    const projectFiles = Array.isArray(state.projectFiles) ? state.projectFiles : [];
    containerEl.innerHTML = `
      <div id="pcw-root" class="card" style="padding:10px;">
        <style>
          #pcw-root * { transition: none !important; }
          #pcw-root *:focus { outline: none !important; box-shadow: none !important; }
          #pcw-root .toolset-group,
          #pcw-root .toolset-group:hover,
          #pcw-root .toolset-group:focus-within,
          #pcw-root .toolset-content,
          #pcw-root .card {
            box-shadow: none !important;
            border-color: rgba(148,163,184,.22) !important;
          }
        </style>
        <div class="lm-title">Point Cloud Workspace</div>
        <div class="small" style="margin-top:6px;">Open local/server point cloud files and preview them in 3D with lightweight sampling.</div>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Dataset</summary>
          <div class="toolset-content">
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:140px;">File Path</label>
              <input id="pcw-file-path" type="text" value="${esc(state.filePath)}" placeholder="C:\\Data\\survey.las" style="flex:1;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:140px;">Preview Points</label>
              <input id="pcw-max-points" type="number" min="1000" max="50000" step="1000" value="${esc(String(state.maxPreviewPoints))}" style="width:140px;" />
            </div>
            <div class="row lm-add-row" style="margin-top:8px;">
              <button id="pcw-load" class="btn primary"${busy ? " disabled" : ""}>Open Dataset</button>
              <button id="pcw-refresh-status" class="btn"${busy ? " disabled" : ""}>Tool Status</button>
            </div>
            <div class="row lm-add-row" style="margin-top:10px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Point Cloud Project Files</label>
              <input id="pcw-project-files-input" type="file" accept=".las,.laz" multiple />
              <div style="display:flex; gap:6px; margin-top:6px; flex-wrap:wrap;">
                <button id="pcw-project-files-add" class="btn"${potreeBusy ? " disabled" : ""}>Add Files</button>
                <button id="pcw-project-add-current" class="btn">Add Current Path</button>
                <button id="pcw-project-generate-all" class="btn"${potreeBusy ? " disabled" : ""}>Generate Potree (All)</button>
                <button id="pcw-project-refresh-jobs" class="btn">Refresh Project Jobs</button>
                <button id="pcw-project-open-first-completed" class="btn">Open First Completed</button>
              </div>
                <div style="margin-top:8px; max-height:150px; overflow:auto; border:1px solid rgba(148,163,184,.22); border-radius:8px; padding:6px;">
                ${projectFiles.length ? projectFiles.map((item) => `
                  <div style="display:flex; gap:6px; align-items:center; margin-bottom:6px;">
                    <div class="small" style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${esc(item.sourcePath)}">${projectJobIcon(item)} ${esc(item.name)} <span style="opacity:.7;">(${esc(item.status || "ready")})</span></div>
                    <button class="btn" data-project-open="${esc(item.id)}">Use</button>
                    <button class="btn"${(String(item?.status || "").toLowerCase() === "completed" && String(item?.viewerUrl || "").trim()) ? "" : " disabled"} data-project-open-viewer="${esc(item.id)}">Open Viewer</button>
                    <button class="btn" data-project-remove="${esc(item.id)}">X</button>
                  </div>
                `).join("") : `<div class="small" style="opacity:.75;">No project files yet.</div>`}
              </div>
            </div>
            <div class="small" style="margin-top:8px; color:${status ? "#cbd5e1" : "#94a3b8"};">${esc(status || "Ready.")}</div>
          </div>
        </details>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Potree Generator</summary>
          <div class="toolset-content">
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:140px;">Dataset Name</label>
              <input id="pcw-potree-name" type="text" value="${esc(state.potreeDatasetName)}" placeholder="LittleCal Point Cloud" style="flex:1;" />
            </div>
            <div class="row lm-add-row" style="margin-top:8px;">
              <input id="pcw-potree-file" type="file" accept=".las,.laz,.xyz,.txt,.csv,.pts" />
            </div>
            <div class="row lm-add-row" style="margin-top:8px;">
              <button id="pcw-potree-start" class="btn primary"${potreeBusy ? " disabled" : ""}>Generate Potree</button>
              <button id="pcw-potree-refresh" class="btn">Refresh Job</button>
              <button id="pcw-potree-open" class="btn"${!potreeJob?.viewerUrl ? " disabled" : ""}>Open Viewer</button>
            </div>
            <div class="row lm-add-row" style="margin-top:8px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">2D Raster Resolution (m)</label>
              <input id="pcw-raster-resolution" type="number" min="0.1" max="10" step="0.1" value="${esc(String(state.rasterResolution || 1))}" style="width:100%;" />
              <button id="pcw-generate-2d-layers" class="btn" style="margin-top:6px;"${busy || potreeBusy ? " disabled" : ""}>Generate DSM/DTM/Hillshade</button>
            </div>
            <div style="margin-top:10px; padding:8px; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
              <div class="small" style="font-weight:600;">2D Raster Styling</div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Color Ramp</label>
                <select id="pcw-raster-ramp" style="width:100%;">
                  ${["terrain", "viridis", "plasma", "inferno", "spectral", "grayscale", "copper", "blue_orange"].map((r) => `<option value="${r}"${String(state.rasterColorRamp || "terrain") === r ? " selected" : ""}>${esc(r.replaceAll("_", " ").replace(/^./, (m) => m.toUpperCase()))}</option>`).join("")}
                </select>
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Stretch Min</label>
                <input id="pcw-raster-min" type="number" step="0.1" value="${esc(String(oneDecimal(state.rasterStretchMin, 0)))}" style="width:100%;" />
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Stretch Max</label>
                <input id="pcw-raster-max" type="number" step="0.1" value="${esc(String(oneDecimal(state.rasterStretchMax, 100)))}" style="width:100%;" />
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Opacity: ${esc(oneDecimal(state.rasterOpacity, 0.92))}</label>
                <input id="pcw-raster-opacity" type="range" min="0" max="1" step="0.05" value="${esc(oneDecimal(state.rasterOpacity, 0.92))}" style="width:100%;" />
              </div>
              <button id="pcw-raster-apply-style" class="btn" style="margin-top:6px;">Apply Raster Style</button>
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <div class="small" style="color:#94a3b8;">Selection/clip tools temporarily disabled while we stabilize style + large-file loading.</div>
            </div>
            <div class="row lm-add-row" style="margin-top:10px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Point Budget</label>
              <input id="pcw-potree-budget" type="number" min="100000" max="30000000" step="100000" value="${esc(String(state.potreePointBudget || 3000000))}" style="width:100%;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Point Size</label>
              <input id="pcw-potree-size" type="number" min="0.1" max="10" step="0.1" value="${esc(String(state.potreePointSize || 0.8))}" style="width:100%;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Point Shape</label>
              <select id="pcw-potree-shape" style="width:100%;">
                <option value="CIRCLE"${String(state.potreePointShape) === "CIRCLE" ? " selected" : ""}>Circle</option>
                <option value="SQUARE"${String(state.potreePointShape) === "SQUARE" ? " selected" : ""}>Square</option>
                <option value="PARABOLOID"${String(state.potreePointShape) === "PARABOLOID" ? " selected" : ""}>Paraboloid</option>
              </select>
            </div>
            <div class="row lm-add-row" style="margin-top:6px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Color By</label>
              <select id="pcw-potree-attribute" style="width:100%;">
                <option value="classification"${String(state.potreeAttribute) === "classification" ? " selected" : ""}>Classification</option>
                <option value="elevation"${String(state.potreeAttribute) === "elevation" ? " selected" : ""}>Elevation</option>
                <option value="rgba"${String(state.potreeAttribute) === "rgba" ? " selected" : ""}>RGB/RGBA</option>
                <option value="intensity"${String(state.potreeAttribute) === "intensity" ? " selected" : ""}>Intensity</option>
              </select>
              <button id="pcw-potree-apply-style" class="btn" style="margin-top:6px;"${!potreeJob?.viewerUrl ? " disabled" : ""}>Apply Live</button>
              <button id="pcw-potree-axis-zoom" class="btn" style="margin-top:6px;"${!potreeJob?.viewerUrl ? " disabled" : ""}>Zoom To Axis</button>
            </div>
            <div class="row lm-add-row" style="margin-top:6px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Axis Overlay</label>
              <label class="small" style="display:inline-flex; align-items:center; gap:8px;">
                <input id="pcw-potree-axis-enabled" type="checkbox" ${state.potreeAxisEnabled !== false ? "checked" : ""} />
                Show XYZ Axes
              </label>
              <label class="small" style="display:inline-flex; align-items:center; gap:8px; margin-left:12px;">
                <input id="pcw-potree-axis-ticks-enabled" type="checkbox" ${state.potreeAxisTicksEnabled !== false ? "checked" : ""} />
                Labels/Ticks
              </label>
            </div>
            <div style="margin-top:10px; padding:8px; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
              <div class="small" style="font-weight:600;">Eye-Dome-Lighting</div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Enable</label>
                <input id="pcw-potree-edl-enabled" type="checkbox" ${state.potreeEdlEnabled ? "checked" : ""} />
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Radius: ${esc(oneDecimal(state.potreeEdlRadius, 1))}</label>
                <input id="pcw-potree-edl-radius" type="range" min="0.1" max="4" step="0.1" value="${esc(oneDecimal(state.potreeEdlRadius, 1))}" style="flex:1;" />
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Strength: ${esc(oneDecimal(state.potreeEdlStrength, 0.3))}</label>
                <input id="pcw-potree-edl-strength" type="range" min="0" max="5" step="0.1" value="${esc(oneDecimal(state.potreeEdlStrength, 0.3))}" style="flex:1;" />
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Opacity: ${esc(oneDecimal(state.potreeEdlOpacity, 1))}</label>
                <input id="pcw-potree-edl-opacity" type="range" min="0" max="1" step="0.05" value="${esc(oneDecimal(state.potreeEdlOpacity, 1))}" style="flex:1;" />
              </div>
            </div>
            <div id="pcw-potree-elev-styling" style="margin-top:10px; padding:8px; border:1px solid rgba(148,163,184,.22); border-radius:8px; display:${showRampStyling ? "block" : "none"};">
              <div class="small" style="font-weight:600;">Value Styling</div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Color Ramp</label>
                <select id="pcw-potree-gradient" style="width:100%;">
                  ${rampOptions.map((r) => `<option value="${r}"${selectedRamp === r ? " selected" : ""}>${esc(r.replaceAll("_", "-").toLowerCase().replace(/^./, (m) => m.toUpperCase()))}</option>`).join("")}
                </select>
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:${showRampStyling ? "block" : "none"};">
                <label class="small" style="display:block; margin-bottom:4px;">${isIntensity ? "Intensity Range" : "Elevation Range"}</label>
                <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px;">
                  <div class="small">Min: ${esc(oneDecimal(elevMin, zMin))}</div>
                  <div class="small" style="text-align:right;">Max: ${esc(oneDecimal(elevMax, zMax))}</div>
                </div>
              </div>
              <div class="row lm-add-row" style="margin-top:8px; display:${showRampStyling ? "block" : "none"};">
                <label class="small" style="display:block; margin-bottom:4px;">Elevation Histogram</label>
                <div id="pcw-potree-hist-wrap" style="width:100%;" data-zmin="${esc(String(histDomainMin))}" data-zmax="${esc(String(histDomainMax))}">
                  <div id="pcw-potree-elev-hist">${histSvg}</div>
                  <div class="small" style="margin-top:4px; opacity:.75;">Drag orange lines to set min/max range.</div>
                </div>
              </div>
              ${ENABLE_ABSOLUTE_Z_CLIP ? `
              <div class="row lm-add-row" style="margin-top:8px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Hide Above Z</label>
                <div style="display:flex; align-items:center; gap:8px;">
                  <input id="pcw-potree-zclip-enabled" type="checkbox" ${state.potreeZClipEnabled ? "checked" : ""} />
                  <input id="pcw-potree-zclip-max-slider" type="range" min="${esc(String(zMin))}" max="${esc(String(zMax))}" step="0.05" value="${esc(oneDecimal(zClipMax, zMax))}" style="flex:1;" />
                  <button id="pcw-potree-zclip-max-down" class="btn" type="button" style="padding:2px 8px; min-width:28px;">-</button>
                  <button id="pcw-potree-zclip-max-up" class="btn" type="button" style="padding:2px 8px; min-width:28px;">+</button>
                  <input id="pcw-potree-zclip-max-readout" type="text" value="${esc(oneDecimal(zClipMax, zMax))}" style="width:72px;" readonly />
                </div>
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Hide Below Z</label>
                <div style="display:flex; align-items:center; gap:8px;">
                  <input id="pcw-potree-zclip-min-slider" type="range" min="${esc(String(zMin))}" max="${esc(String(zMax))}" step="0.05" value="${esc(oneDecimal(zClipMin, zMin))}" style="flex:1;" />
                  <button id="pcw-potree-zclip-min-down" class="btn" type="button" style="padding:2px 8px; min-width:28px;">-</button>
                  <button id="pcw-potree-zclip-min-up" class="btn" type="button" style="padding:2px 8px; min-width:28px;">+</button>
                  <input id="pcw-potree-zclip-min-readout" type="text" value="${esc(oneDecimal(zClipMin, zMin))}" style="width:72px;" readonly />
                </div>
              </div>
              ` : ""}
            </div>
            <div style="margin-top:10px; padding:8px; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
              <div class="small" style="font-weight:600;">Classification Styling</div>
              ${classIds.map((id) => `
              <div class="row lm-add-row" style="margin-top:6px;">
                <label class="small" style="min-width:140px;">Class ${id} (${asprsClassName(id)})</label>
                <input id="pcw-class-vis-${id}" type="checkbox" ${state.potreeClassVisibility?.[id] !== false ? "checked" : ""} />
                <input id="pcw-class-color-${id}" type="color" value="${esc(String(state.potreeClassColors?.[id] || "#9ca3af"))}" style="width:52px; margin-left:10px;" />
              </div>
              `).join("")}
            </div>
            <div class="small" style="margin-top:8px;">
              Job: ${esc(potreeJob?.id || "n/a")} | Status: ${esc(potreeJob?.status || "idle")} | Progress: ${esc(Number.isFinite(Number(potreeJob?.progressPct)) ? `${Number(potreeJob.progressPct)}%` : "0%")}
            </div>
            <div class="small" style="margin-top:4px; color:${potreeJob?.error ? "#fca5a5" : "#cbd5e1"};">
              ${esc(potreeJob?.error || potreeJob?.message || "Upload a file and click Generate Potree.")}
            </div>
            <div class="small" style="margin-top:4px; color:#94a3b8;">
              Uploaded source is auto-linked to Dataset preview.
            </div>
          </div>
        </details>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Summary</summary>
          <div class="toolset-content">
            <div class="small">Format: ${esc(dataset?.format || "n/a")} | Points: ${esc(formatNumber(dataset?.pointCount || 0, 0) || "n/a")} | Sampled: ${esc(formatNumber(points.length, 0) || "0")}</div>
            <div class="small" style="margin-top:4px;">Full Bounds X: ${esc(fullBounds ? `${formatNumber(fullBounds.minX)} to ${formatNumber(fullBounds.maxX)}` : "n/a")}</div>
            <div class="small" style="margin-top:2px;">Full Bounds Y: ${esc(fullBounds ? `${formatNumber(fullBounds.minY)} to ${formatNumber(fullBounds.maxY)}` : "n/a")}</div>
            <div class="small" style="margin-top:2px;">Full Bounds Z: ${esc(fullBounds ? `${formatNumber(fullBounds.minZ)} to ${formatNumber(fullBounds.maxZ)}` : "n/a")}</div>
            <div class="small" style="margin-top:6px; opacity:.8;">Preview Bounds Z: ${esc(previewBounds ? `${formatNumber(previewBounds.minZ)} to ${formatNumber(previewBounds.maxZ)}` : "n/a")}</div>
          </div>
        </details>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>3D Preview</summary>
          <div class="toolset-content">
            <div id="pcw-preview" style="height:360px; border:1px solid rgba(148,163,184,.22); border-radius:10px; background:radial-gradient(circle at 20% 20%, rgba(8,47,73,.5), rgba(2,6,23,.94)); position:relative; overflow:hidden;">
              <div class="small" style="position:absolute; left:10px; bottom:8px; opacity:.7;">Drag to orbit, scroll to zoom</div>
            </div>
          </div>
        </details>
      </div>
    `;
  }

  function refreshHistogramOnly() {
    if (!containerEl) return;
    const fullBounds = potreeJob?.bounds || dataset?.fullBounds || dataset?.bounds || null;
    const points = Array.isArray(dataset?.preview?.points) ? dataset.preview.points : [];
    const zMin = Number.isFinite(Number(fullBounds?.minZ)) ? Number(fullBounds.minZ) : 0;
    const zMax = Number.isFinite(Number(fullBounds?.maxZ)) ? Number(fullBounds.maxZ) : 100;
    const elevMin = Number.isFinite(Number(state.potreeElevationMin)) ? Number(state.potreeElevationMin) : zMin;
    const elevMax = Number.isFinite(Number(state.potreeElevationMax)) ? Number(state.potreeElevationMax) : zMax;
    const zClipMin = Number.isFinite(Number(state.potreeZClipMin)) ? Number(state.potreeZClipMin) : zMin;
    const zClipMax = Number.isFinite(Number(state.potreeZClipMax)) ? Number(state.potreeZClipMax) : zMax;
    const histDomainMin = state.potreeZClipEnabled ? Math.min(zClipMin, zClipMax) : zMin;
    const histDomainMax = state.potreeZClipEnabled ? Math.max(zClipMin, zClipMax) : zMax;
    const histWrap = containerEl.querySelector("#pcw-potree-hist-wrap");
    const histHost = containerEl.querySelector("#pcw-potree-elev-hist");
    if (!histWrap || !histHost) return;
    histWrap.setAttribute("data-zmin", String(histDomainMin));
    histWrap.setAttribute("data-zmax", String(histDomainMax));
    histHost.innerHTML = buildElevationHistogramSvg(points, histDomainMin, histDomainMax, 104, elevMin, elevMax);
  }

  async function renderPreview() {
    const points = Array.isArray(dataset?.preview?.points) ? dataset.preview.points : [];
    if (!containerEl) return;
    const host = containerEl.querySelector("#pcw-preview");
    if (!host) return;
    if (preview.destroy) {
      preview.destroy();
      preview.destroy = null;
    }
    if (!points.length) {
      host.innerHTML = `<div class="small" style="position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#94a3b8;">No preview points available.</div>`;
      return;
    }
    try {
      const libs = await THREE_LIBS.load();
      preview.destroy = mountThreePreview(host, points, libs);
    } catch (error) {
      status = `3D preview failed: ${error?.message || error}`;
      host.innerHTML = `<div class="small" style="position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#fca5a5; text-align:center; padding:12px;">${esc(status)}</div>`;
      render();
    }
  }

  async function refreshStatus() {
    try {
      const payload = await requestJson("/api/point-cloud/status");
      const pdalReady = payload?.tools?.pdal?.installed ? `PDAL ready (${payload.tools.pdal.version || "installed"})` : "PDAL not ready";
      status = `${pdalReady}.`;
    } catch (error) {
      status = error?.message || "Could not read point cloud tool status.";
    } finally {
      render();
    }
  }

  function stopPotreePoll() {
    if (potreePollTimer) {
      clearInterval(potreePollTimer);
      potreePollTimer = null;
    }
  }

  function stopProjectJobsPoll() {
    if (projectJobsPollTimer) {
      clearInterval(projectJobsPollTimer);
      projectJobsPollTimer = null;
    }
  }

  function startProjectJobsPoll() {
    stopProjectJobsPoll();
    projectJobsPollTimer = setInterval(() => {
      refreshProjectFileJobStatuses();
    }, 1500);
  }

  async function refreshPotreeJob() {
    if (!potreeJob?.id) return;
    try {
      const payload = await requestJson(`/api/point-cloud/potree/jobs/${encodeURIComponent(potreeJob.id)}`);
      if (payload?.job) potreeJob = payload.job;
      reconcileRangesToCurrentBounds();
      if (["completed", "failed", "canceled"].includes(String(potreeJob?.status || "").toLowerCase())) {
        potreeBusy = false;
        stopPotreePoll();
      }
    } catch (error) {
      potreeBusy = false;
      stopPotreePoll();
      potreeJob = { ...(potreeJob || {}), error: String(error?.message || error) };
    } finally {
      render();
      await renderPreview();
    }
  }

  async function startPotreeGenerate() {
    pullStateFromForm();
    const fileInput = containerEl?.querySelector("#pcw-potree-file");
    const file = fileInput?.files?.[0];
    potreeBusy = true;
    potreeJob = { id: "", status: "preparing", progressPct: 5, message: "Preparing source..." };
    render();
    try {
      let sourcePath = "";
      if (file) {
        potreeJob = { ...(potreeJob || {}), status: "uploading", progressPct: 8, message: "Uploading source file..." };
        render();
        const uploadRes = await fetch("/api/point-cloud/potree/upload", {
          method: "POST",
          headers: {
            "Content-Type": "application/octet-stream",
            "x-file-name": String(file.name || "point-cloud.las"),
          },
          body: file,
        });
        const uploadBody = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok || !uploadBody?.sourcePath) throw new Error(String(uploadBody?.error || "potree_upload_failed"));
        sourcePath = String(uploadBody.sourcePath || "").trim();
        state.filePath = sourcePath;
        if (!state.potreeDatasetName) state.potreeDatasetName = String(file.name || "Point Cloud");
      } else {
        sourcePath = String(state.filePath || "").trim();
        if (!sourcePath) throw new Error("Select a source file or enter Dataset > File Path.");
      }
      persist();
      render();
      await loadDataset();
      const startRes = await requestJson("/api/point-cloud/potree/jobs", {
        method: "POST",
        body: {
          datasetName: state.potreeDatasetName || file?.name || "Point Cloud",
          sourcePath,
        },
      });
      if (!startRes?.job?.id) throw new Error("potree_job_start_failed");
      potreeJob = startRes.job;
      state.potreeLastJobId = String(potreeJob?.id || "");
      persist();
      stopPotreePoll();
      potreePollTimer = setInterval(refreshPotreeJob, 1200);
      await refreshPotreeJob();
    } catch (error) {
      potreeBusy = false;
      potreeJob = { ...(potreeJob || {}), status: "failed", progressPct: 100, error: String(error?.message || error) };
      render();
    }
  }

  async function loadDataset() {
    pullStateFromForm();
    if (!state.filePath) {
      status = "Enter a dataset file path first.";
      render();
      return;
    }
    busy = true;
    // Prevent stale job bounds/ranges from old datasets while loading a new source.
    potreeJob = null;
    stopPotreePoll();
    status = "Opening dataset...";
    render();
    try {
      const payload = await requestJson("/api/point-cloud/open", {
        method: "POST",
        body: {
          filePath: state.filePath,
          maxPreviewPoints: state.maxPreviewPoints,
        },
      });
      dataset = payload?.dataset || null;
      reconcileRangesToCurrentBounds({ resetToFull: true });
      status = `Loaded ${dataset?.format || "dataset"} with ${formatNumber(dataset?.pointCount || 0, 0)} points.`;
      await refreshOrAttachPotreeJob();
    } catch (error) {
      status = error?.message || "Could not open point cloud dataset.";
      dataset = null;
    } finally {
      busy = false;
      render();
      await renderPreview();
    }
  }

  function onInput(event) {
    if (busy) return;
    const t = event?.target;
    const id = String(t?.id || "");
    if (id === "pcw-potree-gradient") {
      const track = containerEl?.querySelector("#pcw-potree-elev-track");
      const rampTrack = containerEl?.querySelector("#pcw-potree-ramp-track");
      const nextGrad = containerEl?.querySelector("#pcw-potree-gradient")?.value || state.potreeGradient;
      if (track) track.style.background = gradientCss(nextGrad);
      if (rampTrack) rampTrack.style.background = gradientCss(nextGrad);
    }
    if (id === "pcw-potree-edl-radius" || id === "pcw-potree-edl-strength" || id === "pcw-potree-edl-opacity") {
      pullStateFromForm();
      pushPotreeStyleToViewer();
      return;
    }
    if (id === "pcw-potree-zclip-max-slider") {
      const readout = containerEl?.querySelector("#pcw-potree-zclip-max-readout");
      const v = Number(containerEl?.querySelector("#pcw-potree-zclip-max-slider")?.value || state.potreeZClipMax || 0);
      if (readout && Number.isFinite(v)) readout.value = oneDecimal(v, v);
      pullStateFromForm();
      refreshHistogramOnly();
    }
    if (id === "pcw-potree-zclip-min-slider") {
      const readout = containerEl?.querySelector("#pcw-potree-zclip-min-readout");
      const v = Number(containerEl?.querySelector("#pcw-potree-zclip-min-slider")?.value || state.potreeZClipMin || 0);
      if (readout && Number.isFinite(v)) readout.value = oneDecimal(v, v);
      pullStateFromForm();
      refreshHistogramOnly();
    }
    if (id === "pcw-potree-zclip-enabled") {
      pullStateFromForm();
      refreshHistogramOnly();
    }
    if (id === "pcw-potree-attribute") {
      const wrap = containerEl?.querySelector("#pcw-potree-elev-styling");
      const attr = String(containerEl?.querySelector("#pcw-potree-attribute")?.value || "");
      if (wrap) wrap.style.display = (attr === "elevation" || attr === "intensity") ? "block" : "none";
      state.potreeAttribute = attr || "classification";
      const allowed = rampsForAttribute(state.potreeAttribute);
      if (!allowed.includes(String(state.potreeGradient || "").toUpperCase())) {
        state.potreeGradient = allowed[0];
      }
      persist();
      render();
      pullStateFromForm();
      pushPotreeStyleToViewer();
      return;
    }
    pullStateFromForm();
    if (
      id === "pcw-potree-budget" || id === "pcw-potree-size" || id === "pcw-potree-shape" || id === "pcw-potree-attribute" ||
      id === "pcw-potree-axis-enabled" || id === "pcw-potree-axis-ticks-enabled" ||
      id === "pcw-potree-edl-enabled" || id === "pcw-potree-edl-radius" || id === "pcw-potree-edl-strength" || id === "pcw-potree-edl-opacity" ||
      id === "pcw-potree-gradient" ||
      id === "pcw-potree-zclip-enabled" || id === "pcw-potree-zclip-max-slider" || id === "pcw-potree-zclip-max" ||
      id === "pcw-potree-zclip-min-slider" ||
      id.startsWith("pcw-class-vis-") || id.startsWith("pcw-class-color-")
    ) {
      pushPotreeStyleToViewer();
    }
  }

  function onClick(event) {
    if (event.target?.closest?.("#pcw-load")) {
      loadDataset();
      return;
    }
    if (event.target?.closest?.("#pcw-project-files-add")) {
      addProjectFilesFromPicker();
      return;
    }
    if (event.target?.closest?.("#pcw-project-add-current")) {
      pullStateFromForm();
      if (state.filePath) {
        upsertProjectFile({
          name: String(state.filePath.split(/[\\/]/).pop() || "Point Cloud"),
          sourcePath: state.filePath,
          status: "ready",
        });
        status = "Added current path to project files.";
      } else {
        status = "Set a file path first.";
      }
      render();
      return;
    }
    if (event.target?.closest?.("#pcw-project-generate-all")) {
      generatePotreeForProjectFiles();
      return;
    }
    if (event.target?.closest?.("#pcw-project-refresh-jobs")) {
      startProjectJobsPoll();
      refreshProjectFileJobStatuses();
      return;
    }
    if (event.target?.closest?.("#pcw-project-open-first-completed")) {
      const first = (state.projectFiles || []).find((item) => String(item?.status || "").toLowerCase() === "completed" && String(item?.viewerUrl || "").trim());
      if (first?.viewerUrl) {
        openPotreeViewer(first.viewerUrl);
      } else {
        status = "No completed project viewer is ready yet.";
        render();
      }
      return;
    }
    const projectOpenBtn = event.target?.closest?.("[data-project-open]");
    if (projectOpenBtn) {
      const itemId = String(projectOpenBtn.getAttribute("data-project-open") || "");
      const item = (state.projectFiles || []).find((it) => String(it?.id || "") === itemId);
      if (item?.sourcePath) {
        state.filePath = String(item.sourcePath);
        if (!state.potreeDatasetName) state.potreeDatasetName = String(item.name || "");
        persist();
        render();
        loadDataset();
      }
      return;
    }
    const projectRemoveBtn = event.target?.closest?.("[data-project-remove]");
    if (projectRemoveBtn) {
      removeProjectFile(String(projectRemoveBtn.getAttribute("data-project-remove") || ""));
      render();
      return;
    }
    const projectOpenViewerBtn = event.target?.closest?.("[data-project-open-viewer]");
    if (projectOpenViewerBtn) {
      const itemId = String(projectOpenViewerBtn.getAttribute("data-project-open-viewer") || "");
      const item = (state.projectFiles || []).find((it) => String(it?.id || "") === itemId);
      if (item?.viewerUrl) openPotreeViewer(item.viewerUrl);
      return;
    }
    if (event.target?.closest?.("#pcw-refresh-status")) {
      refreshStatus();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-start")) {
      startPotreeGenerate();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-refresh")) {
      refreshOrAttachPotreeJob();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-open")) {
      const url = String(potreeJob?.viewerUrl || "").trim();
      if (url) {
        if (app?.view?.showPotree) {
          app.view.showPotree(url);
          potreeViewerWindow = app?.view?.getPotreeWindow?.() || null;
        } else {
          potreeViewerWindow = window.open(url, "_blank");
        }
        setTimeout(() => {
          potreeViewerWindow = app?.view?.getPotreeWindow?.() || potreeViewerWindow;
          pushPotreeStyleToViewer();
        }, 700);
      }
      return;
    }
    if (event.target?.closest?.("#pcw-potree-apply-style")) {
      pullStateFromForm();
      pushPotreeStyleToViewer();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-axis-zoom")) {
      pushPotreeCommandToViewer("zoom_axis_origin");
      return;
    }
    if (event.target?.closest?.("#pcw-generate-2d-layers")) {
      generateAndLoad2DLayers();
      return;
    }
    if (event.target?.closest?.("#pcw-raster-apply-style")) {
      applyRasterStyleToGeneratedLayers();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-zclip-max-down")) {
      nudgeClipValue("max", -Z_NUDGE_STEP);
      pushPotreeStyleToViewer();
      render();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-zclip-max-up")) {
      nudgeClipValue("max", +Z_NUDGE_STEP);
      pushPotreeStyleToViewer();
      render();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-zclip-min-down")) {
      nudgeClipValue("min", -Z_NUDGE_STEP);
      pushPotreeStyleToViewer();
      render();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-zclip-min-up")) {
      nudgeClipValue("min", +Z_NUDGE_STEP);
      pushPotreeStyleToViewer();
      render();
      return;
    }
    
  }

  function onPointerDown(event) {
    const id = String(event?.target?.id || "");
    const histHost = event?.target?.closest?.("#pcw-potree-hist-wrap");
    if (histHost) {
      const zMin = Number(histHost.getAttribute("data-zmin"));
      const zMax = Number(histHost.getAttribute("data-zmax"));
      let minNow = Number(state.potreeElevationMin ?? zMin);
      let maxNow = Number(state.potreeElevationMax ?? zMax);
      const span = Math.max(1e-9, zMax - zMin);
      const targetEl = containerEl?.querySelector("#pcw-potree-elev-hist svg");
      if (!targetEl || !Number.isFinite(zMin) || !Number.isFinite(zMax)) return;
      const rect = targetEl.getBoundingClientRect();
      const toZ = (clientX) => {
        const t = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)));
        return zMin + (t * span);
      };
      const startZ = toZ(event.clientX);
      let active = Math.abs(startZ - minNow) <= Math.abs(startZ - maxNow) ? "min" : "max";
      const move = (ev) => {
        const z = toZ(ev.clientX);
        if (active === "min") {
          setElevationRangeInUI(Math.min(z, maxNow), maxNow);
          minNow = Number(state.potreeElevationMin);
        } else {
          setElevationRangeInUI(minNow, Math.max(z, minNow));
          maxNow = Number(state.potreeElevationMax);
        }
        pullStateFromForm();
        render();
        pushPotreeStyleToViewer();
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      event.preventDefault();
      return;
    }
    return;
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("change", onInput);
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("pointerdown", onPointerDown);
    }
    try { app?.ui?.setDockOpen?.("right", true); } catch {}
    containerEl = el;
    render();
    renderPreview();
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("change", onInput);
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("pointerdown", onPointerDown);
  }

  function destroy() {
    if (preview.destroy) {
      preview.destroy();
      preview.destroy = null;
    }
    stopPotreePoll();
    stopProjectJobsPoll();
    if (!containerEl) return;
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("change", onInput);
    containerEl.removeEventListener("click", onClick);
    containerEl.removeEventListener("pointerdown", onPointerDown);
    containerEl = null;
  }

  function snapshot() {
    return {
      version: 1,
      state: { ...state },
      potreeJobId: String(potreeJob?.id || ""),
    };
  }

  function restore(data) {
    const next = data?.state;
    if (!next || typeof next !== "object") return;
    state = {
      ...state,
      ...next,
      filePath: String(next.filePath || state.filePath || ""),
      maxPreviewPoints: Math.max(1000, Math.min(50000, Number(next.maxPreviewPoints || state.maxPreviewPoints || 12000) || 12000)),
      potreeDatasetName: String(next.potreeDatasetName || state.potreeDatasetName || ""),
      projectFiles: Array.isArray(next.projectFiles) ? next.projectFiles : (state.projectFiles || []),
    };
    persist();
    render();
    renderPreview();
    const restoredJobId = String(data?.potreeJobId || "").trim();
    if (restoredJobId) {
      potreeJob = { id: restoredJobId, status: "running", progressPct: 0, message: "Restoring job status..." };
      stopPotreePoll();
      potreePollTimer = setInterval(refreshPotreeJob, 1500);
      refreshPotreeJob();
    }
  }

  return { mount, destroy, snapshot, restore };
}

function mountThreePreview(host, points, libs) {
  const { THREE, OrbitControls } = libs;
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x030712);
  const camera = new THREE.PerspectiveCamera(62, 1, 0.1, 1e9);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  if ("outputColorSpace" in renderer && THREE?.SRGBColorSpace) {
    renderer.outputColorSpace = THREE.SRGBColorSpace;
  } else if ("outputEncoding" in renderer && THREE?.sRGBEncoding) {
    renderer.outputEncoding = THREE.sRGBEncoding;
  }
  host.innerHTML = "";
  host.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;

  const bounds = {
    minX: Infinity, minY: Infinity, minZ: Infinity,
    maxX: -Infinity, maxY: -Infinity, maxZ: -Infinity,
  };
  for (const p of points) {
    const x = Number(p?.x);
    const y = Number(p?.y);
    const z = Number(p?.z);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    if (x < bounds.minX) bounds.minX = x;
    if (y < bounds.minY) bounds.minY = y;
    if (z < bounds.minZ) bounds.minZ = z;
    if (x > bounds.maxX) bounds.maxX = x;
    if (y > bounds.maxY) bounds.maxY = y;
    if (z > bounds.maxZ) bounds.maxZ = z;
  }
  const center = {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
    z: (bounds.minZ + bounds.maxZ) / 2,
  };
  const spanX = Math.max(1e-6, bounds.maxX - bounds.minX);
  const spanY = Math.max(1e-6, bounds.maxY - bounds.minY);
  const spanZ = Math.max(1e-6, bounds.maxZ - bounds.minZ);
  const maxSpan = Math.max(spanX, spanY, spanZ);
  const zMin = bounds.minZ;
  const zMax = bounds.maxZ;
  const zDen = Math.max(1e-9, zMax - zMin);

  const positions = new Float32Array(points.length * 3);
  const colors = new Float32Array(points.length * 3);
  let idx = 0;
  for (const p of points) {
    const x = Number(p?.x);
    const y = Number(p?.y);
    const z = Number(p?.z);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    positions[idx * 3 + 0] = x - center.x;
    positions[idx * 3 + 1] = y - center.y;
    positions[idx * 3 + 2] = z - center.z;
    const t = Math.max(0, Math.min(1, (z - zMin) / zDen));
    const r = 0.15 + (0.85 * t);
    const g = 0.35 + (0.55 * (1 - Math.abs(t - 0.5) * 2));
    const b = 1.0 - (0.55 * t);
    colors[idx * 3 + 0] = r;
    colors[idx * 3 + 1] = g;
    colors[idx * 3 + 2] = b;
    idx += 1;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.computeBoundingSphere();
  const material = new THREE.PointsMaterial({
    size: Math.max(maxSpan * 0.0024, 0.6),
    sizeAttenuation: true,
    vertexColors: true,
    opacity: 0.95,
    transparent: true,
  });
  const cloud = new THREE.Points(geometry, material);
  scene.add(cloud);

  const grid = new THREE.GridHelper(maxSpan * 1.2, 16, 0x1e3a8a, 0x0f172a);
  grid.rotation.x = Math.PI / 2;
  grid.position.z = -((spanZ * 0.55) || 0);
  scene.add(grid);

  const lightA = new THREE.DirectionalLight(0xffffff, 0.6);
  lightA.position.set(maxSpan, maxSpan, maxSpan);
  scene.add(lightA);
  scene.add(new THREE.AmbientLight(0x8ec5ff, 0.42));

  function resize() {
    const w = Math.max(120, host.clientWidth || 0);
    const h = Math.max(120, host.clientHeight || 0);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  }

  const distance = Math.max(maxSpan * 1.8, 20);
  camera.position.set(distance, -distance * 0.9, distance * 0.65);
  controls.target.set(0, 0, 0);
  controls.update();
  resize();

  let raf = 0;
  const loop = () => {
    raf = requestAnimationFrame(loop);
    controls.update();
    renderer.render(scene, camera);
  };
  loop();

  let ro = null;
  const onWindowResize = () => resize();
  if (typeof ResizeObserver === "function") {
    ro = new ResizeObserver(() => resize());
    ro.observe(host);
  } else {
    window.addEventListener("resize", onWindowResize);
  }

  return () => {
    try { cancelAnimationFrame(raf); } catch {}
    try { ro?.disconnect?.(); } catch {}
    try { window.removeEventListener("resize", onWindowResize); } catch {}
    try { controls.dispose(); } catch {}
    try { geometry.dispose(); } catch {}
    try { material.dispose(); } catch {}
    try { renderer.dispose(); } catch {}
    try { host.innerHTML = ""; } catch {}
  };
}
